import React, { useState } from 'react';
import { ArrowLeft, Check, Pencil, Plus, X } from 'lucide-react';
import { Link } from 'react-router-dom';

import Navbar from '../components/Navbar';
import useRestauranteConfiguracion from '../hooks/restaurante/useRestauranteConfiguracion';

const ESTADOS_MESA = {
  LIBRE: 'Libre',
  OCUPADA: 'Ocupada',
  FUERA_DE_SERVICIO: 'Fuera de servicio',
};

const mesaOcupada = (mesa) =>
  mesa.estado === 'OCUPADA' ||
  (mesa.sesiones || []).length > 0;

const campo =
  'h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-verde-esmeralda focus:ring-2 focus:ring-verde-esmeralda/20 disabled:bg-gray-100';

const botonSecundario =
  'inline-flex h-9 items-center gap-1.5 rounded-lg border border-gray-300 px-3 text-xs font-bold text-azul-oscuro transition hover:bg-gray-100 disabled:opacity-50';

const botonPrimario =
  'inline-flex h-10 items-center gap-1.5 rounded-lg bg-azul-oscuro px-4 text-sm font-bold text-white transition hover:bg-verde-oscuro disabled:opacity-50';

function Insignia({ children, tono = 'gris' }) {
  const tonos = {
    gris: 'bg-gray-100 text-gray-600',
    verde: 'bg-emerald-50 text-emerald-700',
    ambar: 'bg-amber-50 text-amber-700',
  };

  return (
    <span
      className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${tonos[tono]}`}
    >
      {children}
    </span>
  );
}

function MesaFila({ mesa, salonActivo, guardando, onActualizar }) {
  const [editando, setEditando] = useState(false);
  const [nombre, setNombre] = useState(mesa.nombre);
  const [alias, setAlias] = useState(mesa.alias || '');
  const [capacidad, setCapacidad] = useState(mesa.capacidad);

  const ocupada = mesaOcupada(mesa);

  const empezar = () => {
    setNombre(mesa.nombre);
    setAlias(mesa.alias || '');
    setCapacidad(mesa.capacidad);
    setEditando(true);
  };

  const guardar = async (event) => {
    event.preventDefault();

    if (!nombre.trim()) return;

    const resultado = await onActualizar(mesa.id, {
      nombre: nombre.trim(),
      alias: alias.trim(),
      capacidad: Number(capacidad),
    });

    if (resultado?.ok) {
      setEditando(false);
    }
  };

  if (editando) {
    return (
      <form
        onSubmit={guardar}
        className="flex flex-wrap items-end gap-3 rounded-xl border border-verde-esmeralda/40 bg-emerald-50/40 p-3"
      >
        <label className="flex flex-col gap-1 text-xs font-bold text-gray-500">
          Nombre
          <input
            value={nombre}
            onChange={(event) => setNombre(event.target.value)}
            maxLength={50}
            disabled={guardando}
            autoFocus
            className={`${campo} w-36`}
          />
        </label>

        <label className="flex flex-col gap-1 text-xs font-bold text-gray-500">
          Apodo
          <input
            value={alias}
            onChange={(event) => setAlias(event.target.value)}
            maxLength={80}
            disabled={guardando}
            placeholder="Opcional"
            className={`${campo} w-44`}
          />
        </label>

        <label className="flex flex-col gap-1 text-xs font-bold text-gray-500">
          Capacidad
          <input
            type="number"
            min={1}
            step={1}
            value={capacidad}
            onChange={(event) => setCapacidad(event.target.value)}
            disabled={guardando}
            className={`${campo} w-24`}
          />
        </label>

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={guardando || !nombre.trim()}
            className={botonPrimario}
          >
            <Check size={16} />
            Guardar
          </button>

          <button
            type="button"
            onClick={() => setEditando(false)}
            disabled={guardando}
            className={`${botonSecundario} h-10`}
          >
            <X size={14} />
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 px-4 py-3 ${
        mesa.activo ? 'bg-white' : 'bg-gray-50'
      }`}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <strong
            className={`text-sm ${
              mesa.activo ? 'text-azul-oscuro' : 'text-gray-400'
            }`}
          >
            {mesa.nombre}
          </strong>

          {mesa.alias && (
            <span className="text-sm text-gray-500">
              · {mesa.alias}
            </span>
          )}

          {!mesa.activo && <Insignia>Desactivada</Insignia>}

          {mesa.activo && (
            <Insignia tono={ocupada ? 'ambar' : 'verde'}>
              {ocupada
                ? ESTADOS_MESA.OCUPADA
                : ESTADOS_MESA[mesa.estado] || mesa.estado}
            </Insignia>
          )}
        </div>

        <p className="mt-0.5 text-xs text-gray-500">
          {mesa.capacidad}{' '}
          {mesa.capacidad === 1 ? 'persona' : 'personas'}
        </p>
      </div>

      <div className="flex gap-2">
        {mesa.activo && (
          <button
            type="button"
            onClick={empezar}
            disabled={guardando}
            className={botonSecundario}
          >
            <Pencil size={13} />
            Editar
          </button>
        )}

        {mesa.activo ? (
          <button
            type="button"
            onClick={() =>
              onActualizar(mesa.id, { activo: false })
            }
            disabled={guardando || ocupada}
            title={
              ocupada
                ? 'No se puede desactivar una mesa ocupada'
                : undefined
            }
            className={botonSecundario}
          >
            Desactivar
          </button>
        ) : (
          <button
            type="button"
            onClick={() =>
              onActualizar(mesa.id, { activo: true })
            }
            disabled={guardando || !salonActivo}
            title={
              !salonActivo
                ? 'Reactivá primero el salón'
                : undefined
            }
            className={botonSecundario}
          >
            Reactivar
          </button>
        )}
      </div>
    </div>
  );
}

function SalonTarjeta({
  salon,
  guardando,
  onActualizarSalon,
  onCrearMesa,
  onActualizarMesa,
}) {
  const [renombrando, setRenombrando] = useState(false);
  const [nombreSalon, setNombreSalon] = useState(salon.nombre);

  const [mesaNombre, setMesaNombre] = useState('');
  const [mesaAlias, setMesaAlias] = useState('');
  const [mesaCapacidad, setMesaCapacidad] = useState(4);

  const mesas = salon.mesas || [];
  const tieneOcupadas = mesas.some(
    (mesa) => mesa.activo && mesaOcupada(mesa)
  );

  const guardarNombre = async (event) => {
    event.preventDefault();

    if (!nombreSalon.trim()) return;

    const resultado = await onActualizarSalon(salon.id, {
      nombre: nombreSalon.trim(),
    });

    if (resultado?.ok) {
      setRenombrando(false);
    }
  };

  const agregarMesa = async (event) => {
    event.preventDefault();

    if (!mesaNombre.trim()) return;

    const resultado = await onCrearMesa(salon, {
      nombre: mesaNombre.trim(),
      alias: mesaAlias.trim(),
      capacidad: Number(mesaCapacidad),
    });

    if (resultado?.ok) {
      setMesaNombre('');
      setMesaAlias('');
      setMesaCapacidad(4);
    }
  };

  return (
    <section
      className={`rounded-2xl border bg-white p-5 shadow-sm ${
        salon.activo ? 'border-gray-200' : 'border-dashed border-gray-300'
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        {renombrando ? (
          <form
            onSubmit={guardarNombre}
            className="flex flex-wrap items-center gap-2"
          >
            <input
              value={nombreSalon}
              onChange={(event) =>
                setNombreSalon(event.target.value)
              }
              maxLength={100}
              disabled={guardando}
              autoFocus
              aria-label="Nombre del salón"
              className={`${campo} w-56`}
            />

            <button
              type="submit"
              disabled={guardando || !nombreSalon.trim()}
              className={botonPrimario}
            >
              <Check size={16} />
              Guardar
            </button>

            <button
              type="button"
              onClick={() => setRenombrando(false)}
              disabled={guardando}
              className={`${botonSecundario} h-10`}
            >
              <X size={14} />
              Cancelar
            </button>
          </form>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <h2
              className={`text-xl font-extrabold ${
                salon.activo ? 'text-azul-oscuro' : 'text-gray-400'
              }`}
            >
              {salon.nombre}
            </h2>

            {!salon.activo && <Insignia>Desactivado</Insignia>}

            <span className="text-sm text-gray-500">
              {mesas.length}{' '}
              {mesas.length === 1 ? 'mesa' : 'mesas'}
            </span>
          </div>
        )}

        {!renombrando && (
          <div className="flex gap-2">
            {salon.activo && (
              <button
                type="button"
                onClick={() => {
                  setNombreSalon(salon.nombre);
                  setRenombrando(true);
                }}
                disabled={guardando}
                className={botonSecundario}
              >
                <Pencil size={13} />
                Renombrar
              </button>
            )}

            {salon.activo ? (
              <button
                type="button"
                onClick={() =>
                  onActualizarSalon(salon.id, { activo: false })
                }
                disabled={guardando || tieneOcupadas}
                title={
                  tieneOcupadas
                    ? 'No se puede desactivar un salón con mesas ocupadas'
                    : undefined
                }
                className={botonSecundario}
              >
                Desactivar salón
              </button>
            ) : (
              <button
                type="button"
                onClick={() =>
                  onActualizarSalon(salon.id, { activo: true })
                }
                disabled={guardando}
                className={botonSecundario}
              >
                Reactivar salón
              </button>
            )}
          </div>
        )}
      </div>

      <div className="mt-4 space-y-2">
        {mesas.map((mesa) => (
          <MesaFila
            key={mesa.id}
            mesa={mesa}
            salonActivo={salon.activo}
            guardando={guardando}
            onActualizar={onActualizarMesa}
          />
        ))}

        {!mesas.length && (
          <div className="rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-400">
            Este salón todavía no tiene mesas.
          </div>
        )}
      </div>

      {salon.activo && (
        <form
          onSubmit={agregarMesa}
          className="mt-4 flex flex-wrap items-end gap-3 border-t border-gray-100 pt-4"
        >
          <label className="flex flex-col gap-1 text-xs font-bold text-gray-500">
            Nueva mesa
            <input
              value={mesaNombre}
              onChange={(event) =>
                setMesaNombre(event.target.value)
              }
              maxLength={50}
              disabled={guardando}
              placeholder="Ej: Mesa 5"
              className={`${campo} w-36`}
            />
          </label>

          <label className="flex flex-col gap-1 text-xs font-bold text-gray-500">
            Apodo
            <input
              value={mesaAlias}
              onChange={(event) =>
                setMesaAlias(event.target.value)
              }
              maxLength={80}
              disabled={guardando}
              placeholder="Opcional"
              className={`${campo} w-44`}
            />
          </label>

          <label className="flex flex-col gap-1 text-xs font-bold text-gray-500">
            Capacidad
            <input
              type="number"
              min={1}
              step={1}
              value={mesaCapacidad}
              onChange={(event) =>
                setMesaCapacidad(event.target.value)
              }
              disabled={guardando}
              className={`${campo} w-24`}
            />
          </label>

          <button
            type="submit"
            disabled={guardando || !mesaNombre.trim()}
            className={botonPrimario}
          >
            <Plus size={16} />
            Agregar mesa
          </button>
        </form>
      )}
    </section>
  );
}

export default function RestauranteConfiguracion() {
  const configuracion = useRestauranteConfiguracion();
  const [nuevoSalon, setNuevoSalon] = useState('');

  const crearSalon = async (event) => {
    event.preventDefault();

    if (!nuevoSalon.trim()) return;

    const resultado = await configuracion.crearSalon(
      nuevoSalon.trim()
    );

    if (resultado?.ok) {
      setNuevoSalon('');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 lg:px-8">
        <Link
          to="/restaurante"
          className="inline-flex items-center gap-2 text-sm font-bold text-azul-oscuro hover:text-verde-esmeralda"
        >
          <ArrowLeft size={18} />
          Volver al salón
        </Link>

        <header className="mt-4">
          <p className="text-xs font-bold uppercase tracking-wider text-verde-oscuro">
            Restaurante
          </p>

          <h1 className="text-2xl font-extrabold text-azul-oscuro sm:text-3xl">
            Salones y mesas
          </h1>

          <p className="mt-1 text-sm text-gray-500">
            Desactivar un salón o una mesa los oculta de la
            operación sin borrar su historial. Se pueden
            reactivar en cualquier momento.
          </p>
        </header>

        <form
          onSubmit={crearSalon}
          className="mt-6 flex flex-wrap items-end gap-3 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
        >
          <label className="flex flex-col gap-1 text-xs font-bold text-gray-500">
            Nuevo salón
            <input
              value={nuevoSalon}
              onChange={(event) =>
                setNuevoSalon(event.target.value)
              }
              maxLength={100}
              disabled={configuracion.guardando}
              placeholder="Ej: Terraza"
              className={`${campo} w-64`}
            />
          </label>

          <button
            type="submit"
            disabled={
              configuracion.guardando || !nuevoSalon.trim()
            }
            className={botonPrimario}
          >
            <Plus size={16} />
            Crear salón
          </button>
        </form>

        {configuracion.error && (
          <div
            role="alert"
            className="mt-4 flex items-start justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
          >
            <span>{configuracion.error}</span>

            <button
              type="button"
              onClick={configuracion.limpiarError}
              aria-label="Cerrar aviso"
              className="text-red-500 hover:text-red-700"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {configuracion.cargando && (
          <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-10 text-center text-gray-500">
            Cargando salones...
          </div>
        )}

        {!configuracion.cargando &&
          !configuracion.salones.length &&
          !configuracion.error && (
            <div className="mt-6 rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center text-gray-500">
              Todavía no hay salones. Creá el primero para
              empezar a cargar mesas.
            </div>
          )}

        <div className="mt-6 space-y-5">
          {configuracion.salones.map((salon) => (
            <SalonTarjeta
              key={salon.id}
              salon={salon}
              guardando={configuracion.guardando}
              onActualizarSalon={configuracion.actualizarSalon}
              onCrearMesa={configuracion.crearMesa}
              onActualizarMesa={configuracion.actualizarMesa}
            />
          ))}
        </div>
      </main>
    </div>
  );
}
